import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OrganizationEntity } from 'mj_generatedentities';
import { MetadataLoadingStage, UserContextService } from 'src/app/core/services/user-context.service';
import { ADMIN_CONTACT_ROLE_NAME, OWNER_CONTACT_ROLE_NAME } from 'src/app/core/util';
import { InstanceProviderService } from 'src/app/dashboard/instance-provider.service';

const defaultLoadingMessage = 'Loading organizations...';

@Component({
  selector: 'app-settings-organizations',
  templateUrl: './organizations.component.html',
  styleUrl: './organizations.component.css'
})
export class OrganizationsSettingsComponent implements OnInit {
  organizations: OrganizationEntity[] = [];

  // For controlling the leave confirmation dialog.
  selectedOrganizationToLeave: OrganizationEntity | null = null;
  selectedOrganizationDetails: OrganizationEntity | null = null;

  currentOrganization: OrganizationEntity | null = null;

  isEditable = false;
  isLoading = true;

  loadingMessage = defaultLoadingMessage;

  constructor(
    private userContextService: UserContextService,
    private instanceProvider: InstanceProviderService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.isLoading = true;

    this.userContextService.MetadataLoadingStage$.subscribe(async (stage) => {
      if (stage >= MetadataLoadingStage.OrganizationLoaded) {
        this.organizations = await this.userContextService.GetOrganizationsOfContact();
        this.currentOrganization = this.organizations.find(org => org.ID === this.userContextService.Organization.ID);
      }
    });

    // Sort organizations alphabetically by name.
    this.organizations.sort((a, b) => a.Name.localeCompare(b.Name));
    this.isLoading = false;
  }

  /**
   * Called when the "New Organization" button is clicked.
   */
  newOrganization(): void {
    this.userContextService.GoToPreboarding();
    this.router.navigate(['/onboarding/new-organization']);
  }

  /**
   * Called when the organization name is clicked.
   * Navigates to the OrganizationDetails component.
   * @param organization The organization whose details should be displayed.
   */
  openOrganizationDetails(organization: OrganizationEntity): void {
    this.selectedOrganizationDetails = organization;
    const role = this.getContactRole(organization);
    this.isEditable = this.canEdit(role);
  }

  /**
   * Closes the organization details dialog.
   */
  closeDetails(): void {
    this.selectedOrganizationDetails = null;
  }

  /**
   * Saves the updated organization details.
   * (Replace the stub with your actual update logic.)
   */
  async saveOrganizationDetails(): Promise<void> {
    if (this.selectedOrganizationDetails.Dirty) {
      this.loadingMessage = 'Saving details...';
      this.isLoading = true;
      await this.selectedOrganizationDetails.Save();
      this.isLoading = false;
      this.loadingMessage = defaultLoadingMessage;
    }
  }

  /**
   * Opens the leave confirmation dialog for the selected organization.
   * @param organization The organization to leave.
   */
  openLeaveDialog(organization: OrganizationEntity): void {
    this.selectedOrganizationToLeave = organization;
  }

  /**
   * Confirms the leave action.
   */
  async confirmLeave(): Promise<void> {
    if (this.selectedOrganizationToLeave) {
      this.organizations = this.organizations.filter(
        org => org.ID !== this.selectedOrganizationToLeave.ID
      );

      this.loadingMessage = 'Leaving organization...';
      this.isLoading = true;
      await this.userContextService.LeaveOrganization(this.selectedOrganizationToLeave.ID);
      this.isLoading = false;
      this.loadingMessage = defaultLoadingMessage;

      this.closeDialog();
    }
  }

  /**
   * Cancels the leave action and closes the dialog.
   */
  cancelLeave(): void {
    this.closeDialog();
  }

  /**
   * Called when the "Switch" button is clicked.
   * Changes the Organization context that the user is currently in.
   * @param organization The organization to switch context to.
   */
  async switchOrganization(organization: OrganizationEntity): Promise<void> {
    await this.userContextService.SetCurrentOrganizationContact(organization.ID);
    await this.instanceProvider.TriggerInstanceProviderSetup(organization.ID);
  }

  /**
   * Returns back to the organizations list view.
   */
  backToList(): void {
    this.selectedOrganizationDetails = null;
  }

  private canEdit(role: string): boolean {
    if (role) {
      return role === OWNER_CONTACT_ROLE_NAME || role === ADMIN_CONTACT_ROLE_NAME;
    }
    return false;
  }

  /**
   * Resets dialog state.
   */
  private closeDialog(): void {
    this.selectedOrganizationToLeave = null;
  }

  private getContactRole(organization: OrganizationEntity): string {
    const orgContact = this.userContextService.OrganizationContacts.find(oc => oc.OrganizationID === organization.ID);
    return orgContact?.ContactRole ?? '';
  }
}
